import { addWeeks } from "date-fns";

import { companyId, apiLanguage as language } from "@chef/constants";

import { getDateFromWeekYear, getWeek, getYear } from "@chef/helpers";

import {
  CalendarDocument,
  CalendarQuery,
  CalendarQueryVariables,
  ProductsByCategoriesDocument,
  ProductsByCategoriesQuery,
  ProductsByCategoriesQueryVariables,
} from "./generated";
import {
  createCustomCacheForQueries,
  ICustomQueryInterface,
} from "./customCache";

export const tags = {
  billing: "billing",
  paymentCard: "paymentCard",
  address: "address",
  preference: "preference",
  defaultBasket: "defaultBasket",
  discount: "discount",
  giftcard: "giftcard",
  consent: "consent",
  order: "order",
  timeblock: "timeblock",
  calendar: "calendar",
  preselectedProducts: "preselectedProducts",
  contactPreferences: "contactPreferences",
  recipe: "recipe",
  externalPartner: "externalPartner",
  userInfo: "userInfo",
  loyaltyLevelsByCompany: "loyaltyLevelsByCompany",
  favorites: "favorites",
  preselectorBasket: "preselectorBasket",
  preselectorPreview: "preselectorPreview",
  preselectorJob: "preselectorJob",
  recipeQuickComments: "recipeQuickComments",
};

export type Tags = keyof typeof tags;

const apiWithCustomCache = createCustomCacheForQueries([
  {
    queryName: "productsByCategories",
    queryDocument: ProductsByCategoriesDocument,

    createDataFromCache: (args, cache) => {
      const result: ProductsByCategoriesQuery["productsByCategories"] = [];

      for (const categoryId of args.categoryIds) {
        const key = `${categoryId}-${args.week}-${args.year}`;
        const cachedValue = cache[key];
        if (!cachedValue) {
          return null;
        }

        result.push(
          cachedValue as ProductsByCategoriesQuery["productsByCategories"][0],
        );
      }

      return {
        productsByCategories: result,
      };
    },

    createObjectCacheValue: (args, data) => {
      const cache: {
        [cache: string]: ProductsByCategoriesQuery["productsByCategories"][0];
      } = {};

      if (!data) {
        return cache;
      }

      for (const pbc of data.productsByCategories) {
        const key = `${pbc.productCategoryId}-${args.week}-${args.year}`;
        cache[key] = pbc;
      }

      return cache;
    },
  } as ICustomQueryInterface<
    ProductsByCategoriesQuery,
    ProductsByCategoriesQueryVariables
  >,

  {
    queryName: "calendar",
    queryDocument: CalendarDocument,

    createDataFromCache: (args, cache) => {
      const result: CalendarQuery["calendar"] = [];

      const startDate = getDateFromWeekYear({
        week: args.week,
        year: args.year,
      });

      for (let range = 0; range < (args.range || 0); range++) {
        const currentDate = addWeeks(startDate, range);

        const week = getWeek(currentDate);
        const year = getYear(currentDate);

        const key = `${week}-${year}`;
        const cachedValue = cache[key];
        if (!cachedValue) {
          return null;
        }

        result.push(cachedValue as CalendarQuery["calendar"][0]);
      }

      return {
        calendar: result,
      };
    },

    createObjectCacheValue: (args, { calendar }) => {
      const cache: { [cache: string]: CalendarQuery["calendar"][0] } = {};

      for (const cal of calendar) {
        const key = `${cal.week}-${cal.year}`;
        cache[key] = cal;
      }

      return cache;
    },
  } as ICustomQueryInterface<CalendarQuery, CalendarQueryVariables>,
]);

export const api = apiWithCustomCache.enhanceEndpoints({
  addTagTypes: Object.keys(tags),
  endpoints: {
    billing: {
      providesTags: [
        tags.billing,
        tags.paymentCard,
        tags.address,
        tags.preference,
        tags.defaultBasket,
        tags.discount,
        tags.giftcard,
        tags.consent,
        tags.contactPreferences,
      ],
    },

    order: { providesTags: [tags.order] },
    orders: { providesTags: [tags.order] },

    timeblocks: {
      providesTags: [tags.timeblock],
      extraOptions: { companyId, anonymous: true },
    },

    timeblocksByRangeWithGeo: {
      providesTags: [tags.timeblock],
      extraOptions: { companyId, anonymous: true },
    },

    calendar: { providesTags: [tags.calendar] },
    preferences: {
      providesTags: [tags.preference],
      extraOptions: { companyId, anonymous: true },
    },

    preselectedProductsForWeek: {
      providesTags: [tags.preselectedProducts],
      extraOptions: { companyId, language, anonymous: true },
    },

    preselectorPreview: {
      providesTags: [tags.preselectorPreview],
      extraOptions: { companyId, anonymous: true },
    },

    recipesByProductIdList: {
      extraOptions: { language, companyId, anonymous: true },
      providesTags: [tags.recipe],
    },

    recipeAndSteps: { extraOptions: { language, anonymous: true } },
    recipeAndStepsWithAvailableMenuWeeks: {
      extraOptions: { language, companyId, anonymous: true },
    },
    recipesByTaxonomyIdList: {
      extraOptions: { language, companyId, anonymous: true },
    },
    recipesAgreementRatings: { providesTags: [tags.recipe] },
    recipesAgreementFavorites: { providesTags: [tags.favorites] },

    taxonomyRelations: {
      extraOptions: { companyId, language, anonymous: true },
    },

    productsByCategories: { extraOptions: { companyId, anonymous: true } },

    productsByIds: { extraOptions: { companyId, anonymous: true } },

    productsByVariationIds: {
      extraOptions: { companyId, anonymous: true },
    },

    productCategories: { extraOptions: { companyId, anonymous: true } },

    pickAndMix: { extraOptions: { companyId, language, anonymous: true } },

    defaultCoupon: { extraOptions: { companyId, anonymous: true } },

    billingNorwegian: { providesTags: [tags.externalPartner] },

    userInfo: { providesTags: [tags.userInfo] },

    availablePaymentPartners: { extraOptions: { companyId, anonymous: true } },

    loyaltyLevelsByCompany: {
      extraOptions: { companyId, anonymous: true },
    },

    recipesByIngredientCategories: {
      extraOptions: { companyId, language, anonymous: true },
    },

    ingredientCategoriesByCompany: {
      extraOptions: { companyId, language, anonymous: true },
    },

    recipesByIds: { extraOptions: { language, anonymous: true } },

    seoTaxonomyCollection: {
      extraOptions: { companyId },
    },

    seoTaxonomyBranches: {
      extraOptions: { companyId },
    },

    recipeQuickComments: {
      extraOptions: { companyId, language },
    },
  },
});
